<template>
    <div>
        <div class="photo-gallery-container">
        <!-- Filters Section -->
        <div class="filters-section mb-4">
            <div class="row align-items-end">
                <!-- Event Year Filter -->
                <div class="col-md-3 mb-3">
                    <label class="form-label"><i class="fas fa-calendar mr-1"></i>Event Year</label>
                    <select 
                        v-model="filters.eventYear" 
                        @change="getCategories(filters.eventYear)"
                        class="form-control"
                    >
                        <option value="">All Years</option>
                        <option v-for="data in eventYearOptions" :key="data.value" :value="data.value">
                            {{ data.label }}
                        </option>
                    </select>
                </div>

                <!-- Category Filter -->
                <div class="col-md-3 mb-3">
                    <label class="form-label"><i class="fas fa-tags mr-1"></i>Category</label>
                    <select 
                        v-model="filters.eventDetailId" 
                        class="form-control"
                        :disabled="!filters.eventYear"
                    >
                        <option value="">All Categories</option>
                        <option v-for="data in categories" :key="data.value" :value="data.value">
                            {{ data.label }}
                        </option>
                    </select>
                </div>

                <!-- Action Buttons -->
                <div class="col-md-6 mb-3">
                    <div class="d-flex justify-content-between align-items-center">
                        <div class="filter-actions">
                            <button 
                                type="button" 
                                class="btn btn-primary btn-sm mr-2" 
                                @click="loadPhotos"
                                :disabled="loading"
                            >
                                <i class="fas fa-search mr-1"></i>
                                {{ loading ? 'Loading...' : 'Filter' }}
                            </button>
                            <button 
                                type="button" 
                                class="btn btn-outline-secondary btn-sm" 
                                @click="clearFilters"
                            >
                                <i class="fas fa-times mr-1"></i>
                                Clear
                            </button>
                        </div>
                        
                        <!-- Gallery Controls -->
                        <div class="gallery-controls">
                            <!-- View Mode Toggle -->
                            <div class="btn-group" role="group">
                                <button 
                                    type="button" 
                                    :class="'btn btn-outline-secondary btn-sm ' + (viewMode === 'grid' ? 'active' : '')"
                                    @click="setViewMode('grid')"
                                    title="Grid View"
                                >
                                    <i class="fas fa-th"></i>
                                </button>
                                <button 
                                    type="button" 
                                    :class="'btn btn-outline-secondary btn-sm ' + (viewMode === 'galleria' ? 'active' : '')"
                                    @click="setViewMode('galleria')"
                                    title="Galleria View"
                                >
                                    <i class="fas fa-images"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
                <span class="sr-only">Loading...</span>
            </div>
            <p class="mt-2">Loading photos...</p>
        </div>

        <!-- Grid View -->
        <div v-else-if="viewMode === 'grid' && images.length > 0" class="photo-grid grid-4">
            <PhotoGalleryCard
                v-for="(image, index) in images"
                :key="image.id || index"
                :image-url="image.itemImageSrc"
                :image-title="image.title"
                :image-description="image.alt"
                :image-id="image.id"
                :image-date="image.date"
                :image-size="image.size"
                :show-details="true"
                :show-overlay="true"
                :show-footer="false"
                :show-view-button="true"
                :show-delete-button="true"
                :is-deleting="deletingPhotoId === image.id"
                :aspect-ratio="aspectRatio"
                :class="{ 'deleting': deletingPhotoId === image.id }"
                @delete-image="onDeleteImage"
                @image-error="onImageError"
            />
        </div>

        <!-- Empty State -->
        <div v-else-if="!loading && images.length === 0" class="empty-state">
            <div class="empty-state-content">
                <div class="empty-state-icon">
                    <i class="fas fa-images"></i>
                </div>
                <h3 class="empty-state-title">
                    <span v-if="filters.eventYear || filters.eventDetailId">Data Not Found</span>
                    <span v-else>No Image</span>
                </h3>
                <p class="empty-state-description">
                    <span v-if="filters.eventYear || filters.eventDetailId">
                        No photos match your current filter criteria.<br>
                        Try adjusting your filters or clearing them to see all photos.
                    </span>
                    <span v-else>
                        There are no photos in the gallery yet.<br>
                        Upload some photos to get started.
                    </span>
                </p>
                <div class="empty-state-actions" v-if="filters.eventYear || filters.eventDetailId">
                    <button 
                        type="button" 
                        class="btn btn-outline-primary btn-sm"
                        @click="clearFilters"
                    >
                        <i class="fas fa-times mr-1"></i>
                        Clear Filters
                    </button>
                </div>
            </div>
        </div>

        <!-- Original Galleria View -->
        <div v-else-if="viewMode === 'galleria' && images.length > 0" class="card">
            <div class="card-body">
                <Galleria 
                    :value="images" 
                    :responsiveOptions="responsiveOptions" 
                    :numVisible="5" 
                    containerStyle="max-width: 100%"
                    :showThumbnails="true"
                    :showIndicators="true"
                    :showItemNavigators="true"
                >
                    <template #item="slotProps">
                        <img 
                            :src="slotProps.item.itemImageSrc" 
                            :alt="slotProps.item.alt" 
                            style="width: 100%; display: block;"
                        />
                    </template>
                    <template #thumbnail="slotProps">
                        <img 
                            :src="slotProps.item.thumbnailImageSrc" 
                            :alt="slotProps.item.alt" 
                        />
                    </template>
                </Galleria>
            </div>
        </div>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue'
import Galleria from 'primevue/galleria'
import PhotoGalleryCard from './PhotoGalleryCard.vue'
import apiService from "@/services/ApiService"

export default defineComponent({
    name: "PhotoGalleryList",
    components: {
        Galleria,
        PhotoGalleryCard
    },
    data() {
        return {
            images: [],
            loading: false,
            deletingPhotoId: null, // Track which photo is being deleted
            viewMode: 'grid', // 'grid' or 'galleria'
            aspectRatio: '1:1', // Aspect ratio for images
            errorImages: new Set(), // Track images that failed to load
            
            // Filter properties
            filters: {
                eventYear: '',
                eventDetailId: ''
            },
            categories: [],
            eventYearOptions: [
                { label: "2023", value: "2023" }, 
                { label: "2024", value: "2024" }, 
                { label: "2025", value: "2025" }
            ],
            
            responsiveOptions: [
                {
                    breakpoint: '1300px',
                    numVisible: 4
                },
                {
                    breakpoint: '575px',
                    numVisible: 1
                }
            ]
        }
    },
    methods: {
        // Filter methods
        getCategories(year) {
            if (!year) {
                this.categories = [];
                this.filters.eventDetailId = '';
                return;
            }

            apiService.get(`/PhotoGallery/categories/${year}`)
                .then(response => {
                    this.categories = response.data;
                }).catch(error => {
                    console.error(error);
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.response?.data?.Message || 'Failed to load categories',
                        life: 5000
                    });
                });
        },

        loadPhotos() {
            this.loading = true;
            this.errorImages.clear(); // Reset error tracking for new load
            
            // Build query parameters
            let queryParams = new URLSearchParams();
            if (this.filters.eventYear) {
                queryParams.append('eventYear', this.filters.eventYear);
            }
            if (this.filters.eventDetailId) {
                queryParams.append('eventDetailId', this.filters.eventDetailId);
            }

            const endpoint = queryParams.toString() 
                ? `/PhotoGallery?${queryParams.toString()}`
                : '/PhotoGallery';

            apiService.get(endpoint)
                .then(response => {
                    // The API now returns PhotoGalleryCardModel which matches the component expectations
                    this.images = response.data || [];
                    this.loading = false;
                    
                    // Debug log for empty results
                    if (this.images.length === 0) {
                        console.log('No photos found for current filters:', this.filters);
                    }
                }).catch(error => {
                    console.error('Error loading photos:', error);
                    this.loading = false;
                    this.images = []; // Ensure images is empty array on error
                    
                    this.$toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.response?.data?.Message || 'Failed to load photos',
                        life: 5000
                    });
                });
        },

        clearFilters() {
            this.filters.eventYear = '';
            this.filters.eventDetailId = '';
            this.categories = [];
            this.loadPhotos();
        },
        // View controls
        setViewMode(mode) {
            this.viewMode = mode
        },
        
        onDeleteImage(imageData) {
            console.log('Delete image:', imageData);
            
            // Check if already deleting another photo
            if (this.deletingPhotoId) {
                this.$toast.add({
                    severity: 'warn',
                    summary: 'Please Wait',
                    detail: 'Another photo is being deleted. Please wait.',
                    life: 3000
                });
                return;
            }
            
            // Debug: Check token
            const token = localStorage.getItem('_ipc_user_token_');
            console.log('Token exists:', !!token);
            console.log('Token length:', token ? token.length : 0);
            
            if (token) {
                try {
                    // Decode JWT payload for debugging
                    const parts = token.split('.');
                    if (parts.length === 3) {
                        const payload = JSON.parse(atob(parts[1]));
                        console.log('JWT Payload:', payload);
                        console.log('User ID from token:', payload.userId);
                        console.log('Token expiry:', new Date(payload.exp * 1000));
                        console.log('Token expired?', Date.now() > payload.exp * 1000);
                    }
                } catch (e) {
                    console.error('Error parsing JWT:', e);
                }
            }
            
            // imageData now contains the actual photo ID
            const photoId = imageData.id;
            const photoTitle = imageData.title;
            
            if (!photoId) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Unable to identify photo for deletion',
                    life: 3000
                });
                return;
            }

            // Show confirmation dialog
            if (confirm(`Are you sure you want to delete "${photoTitle || 'this image'}"?\n\nThis action cannot be undone.`)) {
                // Set loading state for this specific photo
                this.deletingPhotoId = photoId;
                
                // Show loading toast
                this.$toast.add({
                    severity: 'info',
                    summary: 'Deleting Photo',
                    detail: `Deleting "${photoTitle || 'image'}"...`,
                    life: 2000
                });
                
                // Call API to soft delete the photo
                console.log('Making DELETE request to:', `/PhotoGallery/${photoId}`);
                
                apiService.delete(`/PhotoGallery/${photoId}`)
                    .then(() => {
                        // Remove photo from local array
                        this.images = this.images.filter(img => img.id !== photoId);
                        
                        this.$toast.add({
                            severity: 'success',
                            summary: 'Photo Deleted',
                            detail: `"${photoTitle || 'Image'}" has been successfully deleted`,
                            life: 3000
                        });
                    })
                    .catch(error => {
                        console.error('Error deleting photo:', error);
                        console.error('Error response status:', error.response?.status);
                        console.error('Error response data:', error.response?.data);
                        console.error('Error response headers:', error.response?.headers);
                        
                        let errorMessage = 'Failed to delete photo. Please try again.';
                        
                        if (error.response?.status === 401) {
                            errorMessage = 'Authentication failed. Please login again.';
                            // Optional: redirect to login
                            // this.$router.push('/login');
                        } else if (error.response?.status === 403) {
                            errorMessage = 'You do not have permission to delete this photo.';
                        } else if (error.response?.status === 404) {
                            errorMessage = 'Photo not found. It may have already been deleted.';
                        }
                        
                        this.$toast.add({
                            severity: 'error',
                            summary: 'Delete Failed',
                            detail: errorMessage,
                            life: 5000
                        });
                    })
                    .finally(() => {
                        // Clear loading state
                        this.deletingPhotoId = null;
                    });
            }
        },
        
        onImageError(imageData) {
            // Create a unique key for this image
            const imageKey = `${imageData.id}_${imageData.url}`
            
            // Only log error once per image
            if (!this.errorImages.has(imageKey)) {
                this.errorImages.add(imageKey)
                console.error('Image failed to load:', imageData)
                
                // No alert/toast - just silently handle with placeholder image
            }
        },

        // Data methods
        getPhotoGalleries() {
            return [
                {
                    itemImageSrc: 'https://primefaces.org/cdn/primevue/images/galleria/galleria1.jpg',
                    thumbnailImageSrc: 'https://primefaces.org/cdn/primevue/images/galleria/galleria1s.jpg',
                    alt: 'Beautiful landscape with mountains and lake',
                    title: 'Mountain Lake View',
                    date: new Date('2024-01-15'),
                    size: 2458673
                },
                {
                    itemImageSrc: 'https://primefaces.org/cdn/primevue/images/galleria/galleria2.jpg',
                    thumbnailImageSrc: 'https://primefaces.org/cdn/primevue/images/galleria/galleria2s.jpg',
                    alt: 'City skyline at sunset',
                    title: 'Urban Sunset',
                    date: new Date('2024-02-20'),
                    size: 1843952
                },
                {
                    itemImageSrc: 'https://primefaces.org/cdn/primevue/images/galleria/galleria3.jpg',
                    thumbnailImageSrc: 'https://primefaces.org/cdn/primevue/images/galleria/galleria3s.jpg',
                    alt: 'Forest path in autumn',
                    title: 'Autumn Forest',
                    date: new Date('2024-03-10'),
                    size: 3245789
                },
                {
                    itemImageSrc: 'https://primefaces.org/cdn/primevue/images/galleria/galleria4.jpg',
                    thumbnailImageSrc: 'https://primefaces.org/cdn/primevue/images/galleria/galleria4s.jpg',
                    alt: 'Ocean waves crashing on rocks',
                    title: 'Ocean Waves',
                    date: new Date('2024-04-05'),
                    size: 2976543
                },
                {
                    itemImageSrc: 'https://primefaces.org/cdn/primevue/images/galleria/galleria5.jpg',
                    thumbnailImageSrc: 'https://primefaces.org/cdn/primevue/images/galleria/galleria5s.jpg',
                    alt: 'Desert dunes at sunrise',
                    title: 'Desert Sunrise',
                    date: new Date('2024-05-12'),
                    size: 2134567
                },
                {
                    itemImageSrc: 'https://primefaces.org/cdn/primevue/images/galleria/galleria6.jpg',
                    thumbnailImageSrc: 'https://primefaces.org/cdn/primevue/images/galleria/galleria6s.jpg',
                    alt: 'Snowy mountain peaks',
                    title: 'Snow Peaks',
                    date: new Date('2024-06-18'),
                    size: 3567890
                },
                {
                    itemImageSrc: 'https://primefaces.org/cdn/primevue/images/galleria/galleria7.jpg',
                    thumbnailImageSrc: 'https://primefaces.org/cdn/primevue/images/galleria/galleria7s.jpg',
                    alt: 'Tropical beach paradise',
                    title: 'Tropical Beach',
                    date: new Date('2024-07-22'),
                    size: 2789123
                },
                {
                    itemImageSrc: 'https://primefaces.org/cdn/primevue/images/galleria/galleria8.jpg',
                    thumbnailImageSrc: 'https://primefaces.org/cdn/primevue/images/galleria/galleria8s.jpg',
                    alt: 'Ancient architecture detail',
                    title: 'Ancient Architecture',
                    date: new Date('2024-08-15'),
                    size: 1987654
                },
                {
                    itemImageSrc: 'https://primefaces.org/cdn/primevue/images/galleria/galleria9.jpg',
                    thumbnailImageSrc: 'https://primefaces.org/cdn/primevue/images/galleria/galleria9s.jpg',
                    alt: 'Wildflower meadow in spring',
                    title: 'Spring Meadow',
                    date: new Date('2024-09-03'),
                    size: 2456789
                },
                {
                    itemImageSrc: 'https://primefaces.org/cdn/primevue/images/galleria/galleria10.jpg',
                    thumbnailImageSrc: 'https://primefaces.org/cdn/primevue/images/galleria/galleria10s.jpg',
                    alt: 'Starry night sky',
                    title: 'Starry Night',
                    date: new Date('2024-10-11'),
                    size: 3123456
                },
                {
                    itemImageSrc: 'https://primefaces.org/cdn/primevue/images/galleria/galleria11.jpg',
                    thumbnailImageSrc: 'https://primefaces.org/cdn/primevue/images/galleria/galleria11s.jpg',
                    alt: 'Waterfall in the jungle',
                    title: 'Jungle Waterfall',
                    date: new Date('2024-11-07'),
                    size: 2834567
                },
                {
                    itemImageSrc: 'https://primefaces.org/cdn/primevue/images/galleria/galleria12.jpg',
                    thumbnailImageSrc: 'https://primefaces.org/cdn/primevue/images/galleria/galleria12s.jpg',
                    alt: 'Historic castle on a hill',
                    title: 'Historic Castle',
                    date: new Date('2024-12-01'),
                    size: 2567890
                }
            ];
        },

        getPhotos() {
            return Promise.resolve(this.getPhotoGalleries());
        }
    },
    mounted() {
        this.loadPhotos();
    }
})
</script>

<style scoped>
.photo-gallery-container {
    padding: 0;
}

/* Filters Section */
.filters-section {
    background: #f8f9fa;
    padding: 1.5rem;
    border-radius: 0.5rem;
    border: 1px solid #dee2e6;
    margin-bottom: 1.5rem;
}

.form-label {
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #495057;
    font-size: 0.875rem;
}

.filter-actions {
    display: flex;
    align-items: center;
}

.gallery-controls {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.gallery-controls .btn {
    border-radius: 0.375rem;
}

.gallery-controls .btn.active {
    background-color: #4e73df;
    border-color: #4e73df;
    color: white;
}

/* Grid Layout */
.photo-grid {
    display: grid;
    gap: 1.5rem;
    margin-bottom: 2rem;
    align-items: stretch; /* Ensure all grid items stretch to same height */
}

/* 4x4 Grid */
.photo-grid.grid-4 {
    grid-template-columns: repeat(4, 1fr);
}

/* Ensure PhotoGalleryCard components fill the grid cell height */
.photo-grid .photo-gallery-card {
    height: 100%;
    display: flex;
    flex-direction: column;
}

/* Deleting state for photo cards */
.photo-grid .photo-gallery-card.deleting {
    position: relative;
    pointer-events: none;
    opacity: 0.6;
}

.photo-grid .photo-gallery-card.deleting::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.7);
    border-radius: 0.5rem;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
}

.photo-grid .photo-gallery-card.deleting::after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 32px;
    height: 32px;
    border: 3px solid rgba(255, 255, 255, 0.3);
    border-top-color: #fff;
    border-radius: 50%;
    transform: translate(-50%, -50%);
    z-index: 11;
    animation: photo-delete-spin 1s linear infinite;
}

@keyframes photo-delete-spin {
    to { transform: translate(-50%, -50%) rotate(360deg); }
}

/* Responsive Grid */
@media (max-width: 1200px) {
    .photo-grid.grid-4 {
        grid-template-columns: repeat(3, 1fr);
    }
}

@media (max-width: 992px) {
    .photo-grid {
        grid-template-columns: repeat(2, 1fr);
        gap: 1rem;
        align-items: stretch;
    }
}

@media (max-width: 768px) {
    .photo-grid {
        grid-template-columns: repeat(2, 1fr);
        gap: 1rem;
        align-items: stretch;
    }
    
    .filters-section .col-md-6 .d-flex {
        flex-direction: column;
        gap: 1rem;
    }
    
    .filter-actions {
        justify-content: center;
        width: 100%;
    }
    
    .gallery-controls {
        justify-content: center;
        flex-direction: column;
        gap: 0.5rem;
        align-items: stretch;
    }
    
    .gallery-controls .btn-group {
        width: 100%;
    }
    
    .gallery-controls .btn {
        flex: 1;
    }
}

@media (max-width: 576px) {
    .photo-grid {
        grid-template-columns: repeat(1, 1fr);
        gap: 0.75rem;
    }
    
    .filters-section {
        padding: 1rem;
    }
    
    .filters-section .row > .col-md-3,
    .filters-section .row > .col-md-6 {
        margin-bottom: 1rem;
    }
    
    .filter-actions .btn {
        font-size: 0.875rem;
        padding: 0.375rem 0.75rem;
    }
    
    .gallery-controls .btn {
        font-size: 0.8rem;
        padding: 0.25rem 0.5rem;
    }
}

@media (max-width: 400px) {
    .photo-grid {
        grid-template-columns: 1fr;
    }
}

/* Loading and Empty States */
.spinner-border {
    width: 3rem;
    height: 3rem;
}

/* Enhanced Empty State */
.empty-state {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    padding: 3rem 1rem;
}

.empty-state-content {
    text-align: center;
    max-width: 500px;
}

.empty-state-icon {
    margin-bottom: 1.5rem;
}

.empty-state-icon i {
    font-size: 4rem;
    color: #6c757d;
    opacity: 0.6;
}

.empty-state-title {
    font-size: 1.5rem;
    font-weight: 600;
    color: #495057;
    margin-bottom: 1rem;
}

.empty-state-description {
    font-size: 1rem;
    color: #6c757d;
    line-height: 1.6;
    margin-bottom: 2rem;
}

.empty-state-actions {
    display: flex;
    justify-content: center;
    gap: 0.75rem;
}

.empty-state-actions .btn {
    padding: 0.5rem 1.5rem;
    font-weight: 500;
}

/* Animation for grid changes */
.photo-grid {
    transition: all 0.3s ease;
}
</style>